import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Plus, Search as SearchIcon, Receipt, Loader2, FileText, IndianRupee,
  ArrowRight, Trash2, Eye, FileSpreadsheet, Link as LinkIcon, AlertTriangle,
} from 'lucide-react';
import { fmtDateIN, fmtINR } from '../components/invoicing/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
];

const DOC_TYPE_TABS = [
  { key: 'all', label: 'All Documents' },
  { key: 'proforma', label: 'Proformas' },
  { key: 'tax_invoice', label: 'Tax Invoices' },
];

const docTypeBadge = (dt) => {
  if (dt === 'tax_invoice') return { cls: 'bg-[#1A1A2E] text-[#E8C97A] border-[#1A1A2E]', label: 'Tax Invoice' };
  return { cls: 'bg-slate-100 text-slate-700 border-slate-300', label: 'Proforma' };
};

const statusStyle = (s) => {
  switch (s) {
    case 'paid': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'sent': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
    case 'draft':
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const fmtINR_localOverride = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; // kept for fallback if shared util missing
// Use shared fmtINR from utils.js — see import above. Local copy removed.

const InvoicesPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [docTypeTab, setDocTypeTab] = useState('all');
  const [query, setQuery] = useState('');
  const [summary, setSummary] = useState(null);

  // Selection + hard delete dialog state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hardDeleteDialog, setHardDeleteDialog] = useState({
    open: false,
    mode: 'single', // 'single' or 'bulk'
    target: null,   // invoice object (single) or null (bulk)
    reason: '',
    cascade: true,
    submitting: false,
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all') params.set('status', tab);
      if (docTypeTab !== 'all') params.set('doc_type', docTypeTab);
      if (query.trim()) params.set('q', query.trim());
      const { data } = await axios.get(`${API}/invoicing/invoices?${params.toString()}`);
      setInvoices(data.invoices || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [tab, docTypeTab, query]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invoicing/invoices/summary`);
      setSummary(data);
    } catch (err) {
      console.warn('Summary load failed', err);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    const t = setTimeout(fetchInvoices, 250);
    return () => clearTimeout(t);
  }, [fetchInvoices]);

  const remove = async (inv, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/invoicing/invoices/${inv.invoice_id}`);
      toast.success('Invoice deleted');
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(invoices.map((i) => i.invoice_id)));
  const clearSelection = () => setSelectedIds(new Set());

  const openSingleHardDelete = (inv, e) => {
    e?.stopPropagation();
    setHardDeleteDialog({ open: true, mode: 'single', target: inv, reason: '', cascade: true, submitting: false });
  };
  const openBulkHardDelete = () => {
    if (selectedIds.size === 0) return;
    setHardDeleteDialog({ open: true, mode: 'bulk', target: null, reason: '', cascade: true, submitting: false });
  };

  const submitHardDelete = async () => {
    const { mode, target, reason, cascade } = hardDeleteDialog;
    if (!reason || reason.trim().length < 5) {
      toast.error('Please enter a reason (at least 5 characters) — required for audit trail.');
      return;
    }
    setHardDeleteDialog((p) => ({ ...p, submitting: true }));
    try {
      if (mode === 'single' && target) {
        const { data } = await axios.post(
          `${API}/invoicing/invoices/${target.invoice_id}/hard-delete`,
          { reason: reason.trim(), cascade },
        );
        toast.success(`Hard-deleted ${data.deleted_ids.length} document${data.deleted_ids.length !== 1 ? 's' : ''}`);
      } else {
        const { data } = await axios.post(
          `${API}/invoicing/_/bulk-hard-delete`,
          { invoice_ids: Array.from(selectedIds), reason: reason.trim(), cascade },
        );
        toast.success(`Hard-deleted ${data.hard_deleted_count} document${data.hard_deleted_count !== 1 ? 's' : ''}`);
        if (data.errors?.length) {
          toast.error(`${data.errors.length} failed — see console`);
          console.warn('Bulk hard delete errors:', data.errors);
        }
        clearSelection();
      }
      setHardDeleteDialog({ open: false, mode: 'single', target: null, reason: '', cascade: true, submitting: false });
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hard delete failed');
      setHardDeleteDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const summaryTiles = useMemo(() => {
    if (!summary) return null;
    const s = summary.by_status || {};
    const tiles = [
      { key: 'draft', label: 'Draft', count: s.draft?.count || 0, total: s.draft?.total || 0, color: 'text-slate-700', bg: 'bg-slate-50', icon: FileText },
      { key: 'sent', label: 'Sent', count: s.sent?.count || 0, total: s.sent?.total || 0, color: 'text-blue-700', bg: 'bg-blue-50', icon: ArrowRight },
      { key: 'paid', label: 'Paid', count: s.paid?.count || 0, total: s.paid?.total || 0, color: 'text-emerald-700', bg: 'bg-emerald-50', icon: IndianRupee },
      { key: 'overdue', label: 'Overdue', count: s.overdue?.count || 0, total: s.overdue?.total || 0, color: 'text-red-700', bg: 'bg-red-50', icon: Receipt },
    ];
    return tiles;
  }, [summary]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <AppSidebar user={user} onLogout={logout} active="invoices" />

      <main className="ml-72 p-8" data-testid="invoices-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-5 h-5 text-[#1E3A8A]" />
              <span className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Billing &middot; Invoices</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
            <p className="text-sm text-slate-500 mt-1">Issue proformas; convert to GST Tax Invoices upon payment.</p>
          </div>
          <Button
            onClick={() => navigate('/invoices/new')}
            className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white shadow-md hover:shadow-lg"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Proforma
          </Button>
        </div>

        {/* Summary tiles */}
        {summaryTiles && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {summaryTiles.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.key} className={`${t.bg} border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow`}
                      onClick={() => setTab(t.key)}
                      data-testid={`summary-tile-${t.key}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${t.color}`}>{t.label}</span>
                      <Icon className={`w-4 h-4 ${t.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{t.count}</p>
                    <p className={`text-xs mt-1 ${t.color}`}>{fmtINR(t.total)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="space-y-3 mb-6">
          {/* Document type tabs */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
            {DOC_TYPE_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setDocTypeTab(t.key)}
                data-testid={`doc-type-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                  docTypeTab === t.key
                    ? 'bg-white text-[#1E3A8A] shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                    tab === t.key
                      ? 'bg-white text-[#1E3A8A] shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-md">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by invoice number or client..."
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-48 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoices...
          </div>
        ) : invoices.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No invoices found</p>
              <p className="text-sm text-slate-400 mt-1">
                {tab !== 'all' ? `No invoices in ${tab} status.` : 'Create your first invoice to get started.'}
              </p>
              <Button onClick={() => navigate('/invoices/new')} className="mt-5" data-testid="empty-new-invoice-btn">
                <Plus className="w-4 h-4 mr-2" /> New Invoice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            {/* Bulk action toolbar */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center justify-between"
                   data-testid="bulk-toolbar">
                <span className="text-sm font-medium text-red-900">
                  {selectedIds.size} selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={clearSelection}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={openBulkHardDelete}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="bulk-hard-delete-btn">
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                    Hard Delete {selectedIds.size}
                  </Button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={invoices.length > 0 && selectedIds.size === invoices.length}
                        onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                        className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                        data-testid="select-all-checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Issue Date</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((inv) => {
                    const dt = docTypeBadge(inv.doc_type || 'proforma');
                    const isSelected = selectedIds.has(inv.invoice_id);
                    return (
                    <tr
                      key={inv.invoice_id}
                      onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${isSelected ? 'bg-red-50/40' : ''}`}
                      data-testid={`invoice-row-${inv.invoice_id}`}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(inv.invoice_id)}
                          className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                          data-testid={`select-${inv.invoice_id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-[13px] text-slate-900">
                        {inv.invoice_number}
                        {inv.linked_tax_invoice_number && (
                          <div className="text-[10px] text-emerald-700 font-mono mt-0.5 flex items-center gap-1">
                            <LinkIcon className="w-2.5 h-2.5" />
                            {inv.linked_tax_invoice_number}
                          </div>
                        )}
                        {inv.source_proforma_number && (
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1">
                            <LinkIcon className="w-2.5 h-2.5" />
                            from {inv.source_proforma_number}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${dt.cls} border text-[10px] uppercase tracking-wide font-semibold`}
                               data-testid={`doc-type-badge-${inv.invoice_id}`}>
                          {dt.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="truncate max-w-[260px]">{inv.client_snapshot?.name || '—'}</div>
                        {inv.client_snapshot?.gstin && (
                          <div className="text-xs text-slate-400 font-mono truncate">{inv.client_snapshot.gstin}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateIN(inv.issue_date)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">
                        {fmtINR(inv.total)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${statusStyle(inv.status)} border capitalize`}>{inv.status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${inv.invoice_id}`); }}
                                  className="h-8 w-8 hover:bg-slate-100"
                                  data-testid={`view-invoice-${inv.invoice_id}`}>
                            <Eye className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={(e) => remove(inv, e)}
                                  className="h-8 w-8 hover:bg-red-50"
                                  title="Delete (standard — blocked on issued tax invoices)"
                                  data-testid={`delete-invoice-${inv.invoice_id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                          {(() => {
                            const isPaidTaxInvoice = inv.doc_type === 'tax_invoice' && inv.status === 'paid';
                            const isSuperAdmin = user?.role === 'super_admin';
                            const blocked = isPaidTaxInvoice && !isSuperAdmin;
                            return (
                              <Button size="icon" variant="ghost" onClick={(e) => openSingleHardDelete(inv, e)}
                                      className={blocked ? 'h-8 w-8 opacity-50 cursor-not-allowed' : 'h-8 w-8 hover:bg-red-100'}
                                      disabled={blocked}
                                      title={blocked
                                        ? 'Only a Super Admin can hard-delete paid tax invoices (GST audit-trail safety)'
                                        : 'Hard Delete (force — bypasses GST guard, audit logged)'}
                                      data-testid={`hard-delete-invoice-${inv.invoice_id}`}>
                                <AlertTriangle className={`w-4 h-4 ${blocked ? 'text-slate-400' : 'text-red-700'}`} />
                              </Button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Hard Delete Dialog */}
        <Dialog open={hardDeleteDialog.open} onOpenChange={(o) => !o && setHardDeleteDialog((p) => ({ ...p, open: false }))}>
          <DialogContent className="max-w-lg" data-testid="hard-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Hard Delete {hardDeleteDialog.mode === 'bulk' ? `${selectedIds.size} invoices` : 'Invoice'}
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                This <strong>permanently deletes</strong> the invoice(s) — bypassing the GST immutability guard.
                The deletion will be recorded in the audit log with your reason.
              </DialogDescription>
            </DialogHeader>

            {hardDeleteDialog.mode === 'single' && hardDeleteDialog.target?.doc_type === 'tax_invoice'
              && hardDeleteDialog.target?.status === 'paid' && (
              <div
                className="bg-purple-50 border-2 border-purple-200 rounded p-3 text-sm flex items-start gap-2"
                data-testid="super-admin-notice"
              >
                <AlertTriangle className="w-4 h-4 text-purple-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-900">Super Admin action</p>
                  <p className="text-purple-800 text-xs mt-0.5">
                    You are deleting a <strong>paid tax invoice</strong> — a privileged operation
                    reserved for Super Admins under GST audit-trail norms. Your role
                    (super_admin) and a full snapshot will be recorded in the audit log.
                  </p>
                </div>
              </div>
            )}

            {hardDeleteDialog.mode === 'single' && hardDeleteDialog.target && (
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm space-y-1">
                <div className="font-mono text-[13px] font-semibold text-slate-900">
                  {hardDeleteDialog.target.invoice_number}
                </div>
                <div className="text-xs text-slate-500">
                  {hardDeleteDialog.target.client_snapshot?.name} · {fmtINR(hardDeleteDialog.target.total)} · {hardDeleteDialog.target.status}
                </div>
                {(hardDeleteDialog.target.linked_tax_invoice_number || hardDeleteDialog.target.source_proforma_number) && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <LinkIcon className="w-3 h-3" />
                    Linked to: {hardDeleteDialog.target.linked_tax_invoice_number || hardDeleteDialog.target.source_proforma_number}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="hd-reason" className="text-sm font-medium">
                Reason <span className="text-red-500">*</span>
                <span className="text-xs text-slate-400 ml-1">(min 5 chars, stored in audit log)</span>
              </Label>
              <Textarea
                id="hd-reason"
                value={hardDeleteDialog.reason}
                onChange={(e) => setHardDeleteDialog((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Test invoice from initial setup — cleanup before go-live"
                rows={2}
                className="mt-1"
                data-testid="hard-delete-reason-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{hardDeleteDialog.reason.trim().length} characters</p>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hardDeleteDialog.cascade}
                onChange={(e) => setHardDeleteDialog((p) => ({ ...p, cascade: e.target.checked }))}
                className="mt-1 w-3.5 h-3.5 accent-red-600"
                data-testid="hard-delete-cascade-checkbox"
              />
              <span>
                <span className="font-medium">Cascade — also delete linked counterpart</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  If checked: deleting a proforma also deletes its issued tax invoice (and vice versa). If unchecked, the counterpart stays but is unlinked.
                </span>
              </span>
            </label>

            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>This action cannot be undone.</strong> Use this only for cleanup of test data or genuine errors. For corrections to legitimate tax invoices, issue a Credit Note instead.
              </span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline"
                      onClick={() => setHardDeleteDialog((p) => ({ ...p, open: false }))}
                      disabled={hardDeleteDialog.submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitHardDelete}
                disabled={hardDeleteDialog.submitting || hardDeleteDialog.reason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="hard-delete-confirm-btn"
              >
                {hardDeleteDialog.submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                  : <><AlertTriangle className="w-4 h-4 mr-2" />Hard Delete Permanently</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default InvoicesPage;
